import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ImagePlus, Loader2, Plus, Search, Trash2, Type } from "lucide-react";

import PrivateReferenceImage from "../masterOrder/PrivateReferenceImage";
import PrivateFontPreviewV2 from "./PrivateFontPreviewV2";
import FontPairingSystemV2 from "./FontPairingSystemV2";
import { fontFaceKey, treatmentStyle } from "./FontPairingSystemV2";
import useTypographyFontFacesV1 from "./useTypographyFontFacesV1";
import {
  createTypographyPreference,
  updateTypographyPreference,
  deleteTypographyPreference,
  getBrandProfileV2,
  getTypographyPreferences,
  listFontCatalog,
  uploadPrivateFontAsset,
  uploadUserMediaFile,
} from "../../lib/brandStudioApi";

const ROLE_OPTIONS = [
  ["signature", "Фирменное имя"],
  ["headline", "Заголовки"],
  ["body", "Основной текст"],
  ["accent", "Акцент"],
  ["decorative", "Декоративный"],
];

const ROLE_LABELS = Object.fromEntries(ROLE_OPTIONS);
const LANGUAGE_OPTIONS = [
  ["both", "Русский + English"],
  ["cyrillic", "Кириллица"],
  ["latin", "Латиница"],
];
const LANGUAGE_LABELS = Object.fromEntries(LANGUAGE_OPTIONS);
const CATEGORY_OPTIONS = [
  ["all", "Все"],
  ["sans_serif", "Без засечек"],
  ["serif", "С засечками"],
  ["handwriting", "Рукописные"],
  ["display", "Акцентные"],
  ["monospace", "Моноширинные"],
];
const CATEGORY_LABELS = Object.fromEntries(CATEGORY_OPTIONS);
const LEGACY_ROLE_LABELS = {
  signature: "Фирменная подпись / имя",
  headline: "Заголовки",
  body: "Основной текст",
  accent: "Акцент",
  character: "Характер",
};
const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_FILE_BYTES = 15 * 1024 * 1024;
const FONT_EXTENSIONS = ["otf", "ttf", "woff", "woff2"];

function errorText(error) {
  return error?.response?.data?.detail || "Не удалось сохранить типографический ориентир.";
}

const catalogEntry = (font, item = {}) => ({ font, treatment: {
  font_weight: item.font_weight || (font.weights?.includes(400) ? 400 : font.weights?.[0] || 400),
  font_style: item.font_style || (font.styles?.includes("normal") ? "normal" : font.styles?.[0] || "normal"),
  letter_spacing_em: item.letter_spacing_em ?? 0,
  text_transform: item.text_transform || "none",
} });
function RoleSample({ entry, role, brandName, fontFamily }) {
  const primary = role === "signature" || !role ? brandName : role === "headline" ? "Красота в деталях" : role === "body" ? "Хороший дизайн помогает увидеть главное. Каждая деталь продолжает характер бренда." : "С любовью к деталям";
  const latin = role === "headline" ? "Made with care" : role === "body" ? "Thoughtful details create a lasting impression." : role === "accent" ? "A personal touch" : "Beauty in every detail";
  return <div className="min-w-0 break-words" style={{ ...treatmentStyle(entry), ...(fontFamily ? { fontFamily } : {}) }} data-testid="typography-role-preview">
    <div className={role === "body" ? "text-[15px] leading-6" : "text-[25px] leading-tight"}>{!entry.font.uploaded && !entry.font.scripts?.includes("cyrillic") && /[А-Яа-яЁё]/.test(primary) ? latin : primary}</div>
    <div className="mt-1 text-[14px] leading-5">{latin}</div>
  </div>;
}
function CatalogSample({ entry, status, children }) {
  const ready = status[fontFaceKey(entry)] === "ready";
  return ready ? children : <p role="status" className="mt-3 text-[12px] text-[#655141]">
    {status[fontFaceKey(entry)] === "error" ? "Шрифт не загрузился. Предпросмотр недоступен." : "Загружаю шрифт…"}
  </p>;
}

function ReferencePicker({ references, selectedMediaId, onSelect }) {
  const ordered = useMemo(
    () =>
      [...references].sort((a, b) => {
        const aType = a.signals?.includes("typography") ? 1 : 0;
        const bType = b.signals?.includes("typography") ? 1 : 0;
        return bType - aType || (a.order || 0) - (b.order || 0);
      }),
    [references],
  );

  if (!ordered.length) return null;

  return (
    <div
      className="mt-3 grid max-h-[320px] grid-cols-3 gap-2 overflow-y-auto pr-1 sm:grid-cols-4 md:grid-cols-5"
      data-testid="typography-preferences-reference-picker"
    >
      {ordered.map((reference) => {
        const active = String(reference.media_id) === String(selectedMediaId);
        return (
          <button
            key={reference.id}
            type="button"
            onClick={() => onSelect(reference)}
            aria-pressed={active}
            className={
              "relative overflow-hidden rounded-[14px] border bg-[#F3EBDD] text-left transition " +
              (active
                ? "border-[#6F543B] ring-2 ring-[#D9C8AE]"
                : "border-[#E1D4C2] hover:border-[#BCA98E]")
            }
            data-testid="typography-preferences-reference-option"
          >
            <div className="aspect-[4/5] overflow-hidden">
              <PrivateReferenceImage reference={reference} className="h-full w-full object-cover" />
            </div>
            {reference.signals?.includes("typography") && (
              <span className="absolute left-2 top-2 rounded-full bg-[#FFFDF9]/92 px-2 py-1 text-[9px] text-[#5D4736]">
                Типографика
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function SavedPreference({ item, onDelete, onEdit, busy, fontStatus }) {
  const hasImage = item.mode === "provided" && item.image_url;
  const family = item.font_family || "";
  return (
    <article
      className="min-w-0 overflow-hidden break-words rounded-[18px] border border-[#E1D4C2] bg-white"
      data-testid="typography-preference-saved"
    >
      {hasImage && (
        <div className="grid min-h-[150px] place-items-center overflow-hidden bg-[#F1E9DE] p-3">
          <PrivateReferenceImage reference={item} className="max-h-[230px] max-w-full object-contain" />
        </div>
      )}
      {item.mode === "uploaded_font" && item.font_asset_url && (
        <div className="border-b border-[#E8DCCB] bg-[#FAF6EF] px-4 py-5" data-testid="uploaded-font-saved-preview">
          <PrivateFontPreviewV2
            assetId={item.font_asset_id}
            fileUrl={item.font_asset_url}
            familyLabel={item.font_family}
            compact
          >{(fontFamily) => <div style={{ fontFamily }}><div className="text-[25px]">Beauty in every detail</div><div className="mt-2 text-[17px]">Красота в деталях</div></div>}</PrivateFontPreviewV2>
        </div>
      )}
      {family && item.mode !== "uploaded_font" && (
        <div className="border-b border-[#E8DCCB] bg-[#FAF6EF] px-4 py-5">
          <CatalogSample entry={catalogEntry({ family }, item)} status={fontStatus}>
          <div
            className="text-[25px] leading-tight text-[#2B211A]"
            style={{
              fontFamily: `"${family}", serif`,
              fontWeight: item.font_weight || 400,
              fontStyle: item.font_style || "normal",
              letterSpacing: `${item.letter_spacing_em || 0}em`,
              textTransform: item.text_transform || "none",
            }}
          >
            Oksana Anokina
          </div>
          <div
            className="mt-2 text-[17px] text-[#4A3728]"
            style={{
              fontFamily: `"${family}", serif`,
              fontWeight: item.font_weight || 400,
              fontStyle: item.font_style || "normal",
              letterSpacing: `${item.letter_spacing_em || 0}em`,
              textTransform: item.text_transform || "none",
            }}
          >
            Лаборатория превращений
          </div>
          </CatalogSample>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[9px] uppercase tracking-[0.16em] text-[#927A5B]">
              {ROLE_LABELS[item.role] || item.role}
            </div>
            <div className="mt-1.5 font-serif text-[21px] leading-tight text-[#2B211A]">
              {item.mode === "provided" ? "Твой ориентир" : item.mode === "uploaded_font" ? family || "Мой шрифт" : family || "Выбранный шрифт"}
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#EEF0DF] px-2.5 py-1 text-[10px] text-[#586044]">
            <Check size={11} /> Сохранено
          </span>
        </div>
        <div className="mt-3 text-[11px] text-[#806B55]">
          {LANGUAGE_LABELS[item.language_requirement] || item.language_requirement}
          {item.mode === "uploaded_font" ? " · права подтверждены" : item.font_license_id ? ` · ${item.font_license_id}` : ""}
          {item.font_weight ? ` · насыщенность ${item.font_weight}` : ""}
          {item.font_style === "italic" ? " · курсив" : ""}
          {item.text_transform === "uppercase" ? " · прописные" : ""}
          {item.letter_spacing_em ? ` · интервал ${item.letter_spacing_em > 0 ? "+" : ""}${item.letter_spacing_em}em` : ""}
        </div>
        {item.note && (
          <p className="mt-3 whitespace-pre-wrap text-[12px] leading-5 text-[#5D4736]">
            {item.note}
          </p>
        )}
        <button
          type="button" disabled={busy} onClick={() => onEdit(item)}
          className="mt-4 mr-4 min-h-11 text-[12px] underline" data-testid="typography-preference-edit">
          Изменить шрифты
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onDelete(item.id)}
          className="mt-4 inline-flex min-h-11 items-center gap-1.5 text-[11.5px] text-[#7A4E43] underline underline-offset-4 disabled:opacity-45"
          data-testid="typography-preference-delete"
        >
          <Trash2 size={12} /> Убрать
        </button>
      </div>
    </article>
  );
}

function FontCatalogPicker({
  items,
  selectedId,
  onSelect,
  search,
  onSearch,
  category,
  onCategory,
  previewTitle,
  fontStatus,
  role,
  entryFor,
}) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (category !== "all" && item.category !== category) return false;
      if (q && !item.family.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, category]);

  return (
    <div className="mt-5 border-t border-[#E8DCCB] pt-5" data-testid="font-catalog-picker">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A6D53]">
            Выбрать шрифт
          </div>
          <p className="mt-1 text-[11.5px] leading-5 text-[#806B55]">
            Показываю только разрешённые open-source шрифты, подходящие под выбранные языки.
          </p>
        </div>
        <label className="relative block md:w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B856E]" />
          <input
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Найти шрифт"
            className="min-h-10 w-full rounded-full border border-[#D8C7AE] bg-white pl-9 pr-4 text-[12px] text-[#3E2E20] outline-none focus:border-[#8C6C49]"
            data-testid="font-catalog-search"
          />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {CATEGORY_OPTIONS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={category === value}
            onClick={() => onCategory(value)}
            className={
              "min-h-11 rounded-full border px-3.5 py-2 text-[11px] transition " +
              (category === value
                ? "border-[#8B6B4D] bg-[#F3E9DB] text-[#4A3728]"
                : "border-[#DCCDB8] bg-white text-[#6B5543]")
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-4 grid max-h-[640px] gap-3 overflow-y-auto pr-1 md:grid-cols-2">
        {filtered.map((font) => {
          const active = selectedId === font.id;
          return (
            <button
              key={font.id}
              type="button"
              onClick={() => onSelect(font)}
              aria-pressed={active}
              className={
                "rounded-[16px] border p-4 text-left transition " +
                (active
                  ? "border-[#6F543B] bg-[#F7F1E8] ring-2 ring-[#D9C8AE]"
                  : "border-[#E1D4C2] bg-white hover:border-[#BCA98E]")
              }
              data-testid="font-catalog-option"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-[10px] uppercase tracking-[0.13em] text-[#927A5B]">
                  {font.family}
                </div>
                <span className="rounded-full bg-[#F3E9DB] px-2 py-1 text-[9px] text-[#6B5543]">
                  {CATEGORY_LABELS[font.category] || font.category}
                </span>
              </div>
              <CatalogSample entry={entryFor(font)} status={fontStatus}>
                <div className="mt-4"><RoleSample entry={entryFor(font)} role={role} brandName={previewTitle || "Oksana Anokina"} /></div>
              </CatalogSample>
              <div className="mt-4 flex flex-wrap gap-1.5 text-[9.5px] text-[#806B55]">
                <span>{font.scripts.includes("cyrillic") ? "Кириллица + Latin" : "Latin"}</span>
                <span>·</span>
                <span>{font.license_id}</span>
              </div>
            </button>
          );
        })}
      </div>

      {!filtered.length && (
        <div className="mt-4 rounded-[14px] border border-[#E1D4C2] bg-[#F7F1E8] p-4 text-[12px] text-[#655141]">
          По этим фильтрам шрифтов не найдено.
        </div>
      )}
    </div>
  );
}

export default function TypographyPreferencesInlineV2({ references = [], showSystems = true, compact = false, actionArea, onStateChanged, onSaved, onDraftStateChanged }) {
  const pickerRef = useRef(null);
  const ownFontPickerRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [state, setState] = useState({ preferences: [], legacy_anchor: null });
  const [brandName, setBrandName] = useState("Oksana Anokina");
  const [fontCatalog, setFontCatalog] = useState([]);
  const [adding, setAdding] = useState(false);
  const [mode, setMode] = useState(null);
  const [role, setRole] = useState(null);
  const [languageRequirement, setLanguageRequirement] = useState("both");
  const [selectedMediaId, setSelectedMediaId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFontId, setSelectedFontId] = useState(null);
  const [selectedFontAsset, setSelectedFontAsset] = useState(null);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [fontSearch, setFontSearch] = useState("");
  const [fontCategory, setFontCategory] = useState("all");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [error, setError] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [privateReady, setPrivateReady] = useState(false);
  const privateStatus = useCallback((assetId, value) => setPrivateReady(value === "ready"), []);
  const entryFor = (font) => catalogEntry(font, editingItem && editingItem.font_catalog_id === font.id ? editingItem : {});
  const selectedEntry = mode === "choose_font" ? entryFor(fontCatalog.find((font) => font.id === selectedFontId) || { family: "" })
    : { font: { uploaded: true, family: selectedFontAsset?.display_name }, treatment: {
      font_weight: editingItem && editingItem.font_asset_id === selectedFontAsset?.id ? editingItem.font_weight ?? 400 : 400,
      font_style: editingItem && editingItem.font_asset_id === selectedFontAsset?.id ? editingItem.font_style || "normal" : "normal",
      letter_spacing_em: editingItem && editingItem.font_asset_id === selectedFontAsset?.id ? editingItem.letter_spacing_em ?? 0 : 0,
      text_transform: editingItem && editingItem.font_asset_id === selectedFontAsset?.id ? editingItem.text_transform || "none" : "none",
    } };
  useEffect(() => { onDraftStateChanged?.({ dirty: Boolean(mode || note || selectedMediaId || selectedFontId || selectedFontAsset || rightsConfirmed), busy }); },
    [mode, note, selectedMediaId, selectedFontId, selectedFontAsset, rightsConfirmed, busy, onDraftStateChanged]);

  const previewEntries = [
    ...fontCatalog.map(entryFor),
    ...state.preferences.filter((item) => item.mode === "choose_font" && item.font_family).map((item) =>
      catalogEntry({ id: item.font_catalog_id, family: item.font_family, scripts: item.language_requirement === "latin" ? ["latin"] : ["cyrillic", "latin"] }, item)),
  ];
  const fontStatus = useTypographyFontFacesV1([{ roles: Object.fromEntries(previewEntries.map((entry, index) => [index, entry])) }]);

  const load = useCallback(async () => {
    setStatus("loading");
    setError("");
    try {
      const [savedState, profile] = await Promise.all([
        getTypographyPreferences(),
        getBrandProfileV2().catch(() => null),
      ]);
      setState(savedState);
      setBrandName(profile?.brand_name || "Oksana Anokina");
      setStatus("ready");
    } catch (requestError) {
      setError(errorText(requestError));
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (mode !== "choose_font") return undefined;
    let cancelled = false;
    setCatalogBusy(true);
    listFontCatalog({ language_requirement: languageRequirement })
      .then((items) => {
        if (cancelled) return;
        setFontCatalog(items || []);
        setSelectedFontId((current) => items.some((item) => item.id === current) ? current : null);
      })
      .catch((requestError) => {
        if (!cancelled) setError(errorText(requestError));
      })
      .finally(() => {
        if (!cancelled) setCatalogBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, languageRequirement]);


  const resetForm = () => {
    setEditingItem(null);
    setAdding(false);
    setMode(null);
    setRole(null);
    setLanguageRequirement("both");
    setSelectedMediaId(null);
    setSelectedImage(null);
    setSelectedFontId(null);
    setSelectedFontAsset(null);
    setRightsConfirmed(false);
    setFontSearch("");
    setFontCategory("all");
    setNote("");
    setError("");
  };

  const begin = (nextMode, nextRole = null) => {
    setEditingItem(null);
    setAdding(true);
    setMode(nextMode);
    setRole(nextRole);
    setLanguageRequirement("both");
    setSelectedMediaId(null);
    setSelectedImage(null);
    setSelectedFontId(null);
    setSelectedFontAsset(null);
    setRightsConfirmed(false);
    setFontSearch("");
    setFontCategory("all");
    setNote("");
    setError("");
  };

  const chooseReference = (reference) => {
    setSelectedMediaId(reference.media_id);
    setSelectedImage(reference);
    setError("");
  };

  const edit = (item) => {
    begin(item.mode, item.role);
    setEditingItem(item);
    setLanguageRequirement(item.language_requirement || "both");
    setSelectedFontId(item.font_catalog_id || null);
    setSelectedMediaId(item.media_id || null);
    setSelectedImage(item.mode === "provided" ? item : null);
    setSelectedFontAsset(item.mode === "uploaded_font" ? { id: item.font_asset_id, file_url: item.font_asset_url, display_name: item.font_family } : null);
    setRightsConfirmed(item.mode === "uploaded_font");
    setNote(item.note || "");
  };

  const upload = async (event) => {
    const [file] = Array.from(event.target.files || []);
    event.target.value = "";
    if (!file || busy) return;
    if (!ALLOWED_TYPES.has(file.type) || file.size > MAX_FILE_BYTES) {
      setError("Можно загрузить JPG, PNG или WebP до 15 МБ.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const media = await uploadUserMediaFile(file, { title: "Типографический ориентир бренда" });
      setSelectedMediaId(media.id);
      setSelectedImage({
        id: media.id,
        media_id: media.id,
        image_url: media.file_url,
        width: media.width,
        height: media.height,
      });
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  const uploadOwnFont = async (event) => {
    const [file] = Array.from(event.target.files || []);
    event.target.value = "";
    if (!file || busy) return;
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!FONT_EXTENSIONS.includes(extension || "")) {
      setError("Можно загрузить OTF, TTF, WOFF или WOFF2.");
      return;
    }
    if (!rightsConfirmed) {
      setError("Сначала подтверди право использовать и загружать этот шрифт.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const asset = await uploadPrivateFontAsset(file, true);
      setSelectedFontAsset(asset);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (busy || !mode) return;
    if (!role) {
      setError("Выбери, где использовать шрифт");
      return;
    }
    if (mode === "provided" && !selectedMediaId) {
      setError("Выбери пример из референсов или загрузи отдельный.");
      return;
    }
    if (mode === "choose_font" && !selectedFontId) {
      setError("Выбери конкретный шрифт из каталога.");
      return;
    }
    if (mode === "uploaded_font" && !selectedFontAsset?.id) {
      setError("Загрузи свой файл шрифта.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const payload = {
        role,
        mode,
        media_id: mode === "provided" ? selectedMediaId : null,
        font_catalog_id: mode === "choose_font" ? selectedFontId : null,
        font_asset_id: mode === "uploaded_font" ? selectedFontAsset.id : null,
        note: note.trim(),
        language_requirement: languageRequirement,
        origin: "user_explicit",
        ...(mode !== "provided" ? selectedEntry.treatment : {}),
      };
      if (editingItem) await updateTypographyPreference(editingItem.id, payload);
      else await createTypographyPreference(payload);
      setState(await getTypographyPreferences());
      await onStateChanged?.();
      resetForm();
      onSaved?.();
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await deleteTypographyPreference(id);
      setState(await getTypographyPreferences());
      await onStateChanged?.();
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  if (status === "loading") {
    return (
      <section className="mt-10 rounded-[20px] border border-[#E1D4C2] bg-[#F8F3EB] p-5 md:p-7" data-testid="typography-preferences-inline-loading">
        <div className="inline-flex items-center gap-2 text-[12.5px] text-[#796652]">
          <Loader2 size={15} className="animate-spin" /> Открываю типографические ориентиры…
        </div>
      </section>
    );
  }

  if (status === "error") {
    return (
      <section className="mt-10 rounded-[20px] border border-[#E2CFC8] bg-[#FFF8F4] p-5 md:p-7">
        <h3 className="font-serif text-[25px] text-[#2B211A]">Не удалось открыть типографику</h3>
        <p className="mt-2 text-[12px] leading-5 text-[#6B443A]">{error}</p>
        <button type="button" onClick={load} className="mt-4 min-h-10 rounded-full border border-[#CDB899] bg-white px-4 text-[12px] text-[#4A3728]">
          Повторить
        </button>
      </section>
    );
  }

  const actions = adding && mode ? <div className="max-h-[48dvh] min-w-0 overflow-y-auto border-t border-[#E8DCCB] p-4" data-testid="typography-role-actions">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A6D53]">Где использовать шрифт</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(compact ? ROLE_OPTIONS.filter(([value]) => value !== "decorative" || role === "decorative") : ROLE_OPTIONS).map(([value, label]) => (
              <button key={value} type="button" aria-pressed={role === value} onClick={() => { setRole(value); setError(""); }} className={"min-h-11 rounded-full border px-3.5 py-2 text-[11.5px] transition " + (role === value ? "border-[#8B6B4D] bg-[#F3E9DB] text-[#4A3728]" : "border-[#DCCDB8] bg-white text-[#6B5543]")} data-testid={"typography-preferences-role-" + value}>
                {label}
              </button>
            ))}
          </div>

    {selectedFontId && mode === "choose_font" && <div className="mt-3" data-testid="typography-selected-role-sample">
      <div className="mb-1 text-[12px] text-[#655141]">{selectedEntry.font.family} · {role ? ROLE_LABELS[role] : "Выбери назначение"}</div>
      <CatalogSample entry={selectedEntry} status={fontStatus}><RoleSample entry={selectedEntry} role={role} brandName={brandName} /></CatalogSample>
    </div>}
    {selectedFontAsset && mode === "uploaded_font" && <PrivateFontPreviewV2 assetId={selectedFontAsset.id} fileUrl={selectedFontAsset.file_url} onStatus={privateStatus} compact>
      {(fontFamily) => <RoleSample entry={selectedEntry} role={role} brandName={brandName} fontFamily={fontFamily} />}
    </PrivateFontPreviewV2>}
          {error && <p className="mt-4 rounded-[12px] border border-[#E2CFC8] bg-[#FFF8F4] p-3 text-[12px] text-[#7A4E43]">{error}</p>}

          <div className="mt-5 flex flex-wrap gap-2">
            <button type="button" onClick={save} disabled={busy || (mode === "choose_font" && (!selectedFontId || fontStatus[fontFaceKey(selectedEntry)] !== "ready")) || (mode === "uploaded_font" && !privateReady)} className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[#3D2A20] px-5 text-[12.5px] text-[#FFF8ED] disabled:opacity-45" data-testid="typography-preferences-save">
              {busy && <Loader2 size={14} className="animate-spin" />}
              {mode === "provided" ? "Сохранить ориентир" : mode === "uploaded_font" ? "Сохранить мой шрифт" : "Сохранить шрифт"}
            </button>
            <button type="button" onClick={resetForm} disabled={busy} className="min-h-11 rounded-full border border-[#D8C7AE] bg-white px-4 text-[12px] text-[#4A3728] disabled:opacity-45">
              Отмена
            </button>
          </div>
  </div> : null;

  const hasSaved = Boolean(state.legacy_anchor) || Boolean(state.preferences?.length);

  return (
    <section className={compact ? "min-w-0" : "mt-10 overflow-hidden rounded-[20px] border border-[#DCCDB8] bg-[#F6EFE5] px-5 py-6 md:px-7 md:py-7"} data-testid="typography-preferences-inline">
      {!compact && <div className="max-w-3xl">
        <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">ТИПОГРАФИЧЕСКИЙ ХАРАКТЕР</div>
        <h3 className="mt-3 font-serif text-[27px] leading-tight text-[#2B211A] md:text-[31px]">
          Добавь шрифты и характеры, которые тебе близки
        </h3>
        <p className="mt-3 text-[13px] leading-6 text-[#655141]">
          Можно сохранить несколько ориентиров для разных задач: фирменной подписи,
          заголовков, основного текста, рукописного акцента или декоративных деталей.
          Они не обязаны быть одним шрифтом — Brand Studio соберёт совместимую систему.
        </p>
      </div>}

      <details open={!adding} className="mt-4" data-testid="typography-editor-saved">
        <summary className="min-h-11 cursor-pointer text-[13px]">Сохранённые шрифты и ориентиры</summary>
      {state.legacy_anchor && (
        <div className="mt-6 grid gap-4 rounded-[18px] border border-[#DCCDB8] bg-[#FFFDF9] p-4 md:grid-cols-[180px_1fr]" data-testid="typography-preferences-legacy-anchor">
          {state.legacy_anchor.mode === "provided" && state.legacy_anchor.image_url ? (
            <div className="grid min-h-[150px] place-items-center overflow-hidden rounded-[14px] bg-[#EFE6D9] p-3">
              <PrivateReferenceImage reference={state.legacy_anchor} className="max-h-[220px] max-w-full object-contain" />
            </div>
          ) : (
            <div className="grid min-h-[150px] place-items-center rounded-[14px] bg-[#EFE6D9]">
              <Type size={26} strokeWidth={1.4} className="text-[#8A6D53]" />
            </div>
          )}
          <div className="self-center">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[11px] text-[#586044]">
              <Check size={12} /> Твой основной ориентир сохранён
            </div>
            {!!state.legacy_anchor.roles?.length && (
              <div className="mt-3 flex flex-wrap gap-2">
                {state.legacy_anchor.roles.map((item) => (
                  <span key={item} className="rounded-full border border-[#DCCDB8] bg-white px-3 py-1.5 text-[10.5px] text-[#5D4736]">
                    {LEGACY_ROLE_LABELS[item] || item}
                  </span>
                ))}
              </div>
            )}
            {state.legacy_anchor.note && <p className="mt-3 text-[12.5px] leading-5 text-[#5D4736]">{state.legacy_anchor.note}</p>}
          </div>
        </div>
      )}

      {!!state.preferences?.length && (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="typography-preferences-saved-list">
          {state.preferences.map((item) => (
            <SavedPreference key={item.id} item={item} onDelete={remove} onEdit={edit} busy={busy} fontStatus={fontStatus} />
          ))}
        </div>
      )}

      </details>

      {hasSaved && showSystems && (
        <FontPairingSystemV2
          preferences={state.preferences}
          legacyAnchor={state.legacy_anchor}
          brandName={brandName}
          onChanged={load}
          onChooseRole={(nextRole) => begin("choose_font", nextRole)}
        />
      )}

      {!adding && (
        <div className="mt-6">
          {hasSaved ? (
            <button type="button" onClick={() => setAdding(true)} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#BFA98D] bg-[#FFFDF9] px-5 text-[12.5px] text-[#4A3728]" data-testid="typography-preferences-add-more">
              <Plus size={14} /> Добавить шрифт
            </button>
          ) : (
            <p className="text-[12.5px] leading-5 text-[#806B55]">
              Своего шрифта может не быть — это нормально. Можно выбрать подходящий из каталога.
            </p>
          )}
        </div>
      )}

      {(!hasSaved || adding) && !mode && (
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <button type="button" onClick={() => begin("provided")} className="rounded-[17px] border border-[#CDB899] bg-[#FFFDF9] p-4 text-left transition hover:border-[#9A7A58]" data-testid="typography-preferences-mode-provided">
            <div className="font-serif text-[21px] text-[#2B211A]">У меня есть ориентир</div>
            <p className="mt-1.5 text-[11.5px] leading-5 text-[#806B55]">Выбрать пример из этой доски или загрузить отдельный.</p>
          </button>
          <button type="button" onClick={() => begin("choose_font")} className="rounded-[17px] border border-[#CDB899] bg-[#FFFDF9] p-4 text-left transition hover:border-[#9A7A58]" data-testid="typography-preferences-mode-choose-font">
            <div className="font-serif text-[21px] text-[#2B211A]">Выбрать шрифт</div>
            <p className="mt-1.5 text-[11.5px] leading-5 text-[#806B55]">Откроется реальный каталог шрифтов с визуальным примером.</p>
          </button>
          <button type="button" onClick={() => begin("uploaded_font")} className="rounded-[17px] border border-[#CDB899] bg-[#FFFDF9] p-4 text-left transition hover:border-[#9A7A58]" data-testid="typography-preferences-mode-uploaded-font">
            <div className="font-serif text-[21px] text-[#2B211A]">Добавить свой шрифт</div>
            <p className="mt-1.5 text-[11.5px] leading-5 text-[#806B55]">Для шрифта, на использование которого у тебя есть права.</p>
          </button>
        </div>
      )}

      {adding && mode && (
        <div className="mt-6 rounded-[18px] border border-[#DCCDB8] bg-[#FFFDF9] p-4 md:p-5" data-testid="typography-preferences-editor">
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A6D53]">Языки</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {LANGUAGE_OPTIONS.map(([value, label]) => (
                <button key={value} type="button" aria-pressed={languageRequirement === value} onClick={() => setLanguageRequirement(value)} className={"min-h-11 rounded-full border px-3.5 py-2 text-[11.5px] transition " + (languageRequirement === value ? "border-[#8B6B4D] bg-[#F3E9DB] text-[#4A3728]" : "border-[#DCCDB8] bg-white text-[#6B5543]")}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {mode === "provided" && (
            <div className="mt-5 border-t border-[#E8DCCB] pt-5">
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A6D53]">Выбрать из твоих референсов</div>
              <ReferencePicker references={references} selectedMediaId={selectedMediaId} onSelect={chooseReference} />
              <input ref={pickerRef} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={upload} data-testid="typography-preferences-upload-input" />
              <button type="button" onClick={() => pickerRef.current?.click()} disabled={busy} className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-full border border-[#CDB899] bg-white px-4 text-[12px] text-[#4A3728] disabled:opacity-45" data-testid="typography-preferences-upload">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                Добавить отдельный пример
              </button>
              {selectedImage && (
                <div className="mt-4 grid max-w-[540px] gap-4 rounded-[14px] border border-[#E1D4C2] bg-[#F7F1E8] p-3 sm:grid-cols-[140px_1fr]">
                  <div className="grid min-h-[130px] place-items-center overflow-hidden rounded-[12px] bg-[#E9E0D4]">
                    <PrivateReferenceImage reference={selectedImage} className="max-h-[190px] max-w-full object-contain" />
                  </div>
                  <p className="self-center text-[12px] leading-5 text-[#655141]">Этот пример станет ориентиром только для выбранной роли.</p>
                </div>
              )}
            </div>
          )}

          {mode === "uploaded_font" && (
            <div className="mt-5 border-t border-[#E8DCCB] pt-5" data-testid="own-font-upload">
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A6D53]">Твой лицензированный шрифт</div>
              <p className="mt-2 max-w-2xl text-[12px] leading-5 text-[#655141]">
                Файл будет приватным и останется только внутри этого Brand Profile. Он не попадёт в общий каталог.
              </p>
              <label className="mt-4 flex items-start gap-3 rounded-[14px] border border-[#DCCDB8] bg-[#F7F1E8] p-4 text-[12px] leading-5 text-[#4A3728]">
                <input
                  type="checkbox"
                  checked={rightsConfirmed}
                  onChange={(event) => setRightsConfirmed(event.target.checked)}
                  className="mt-1"
                  data-testid="own-font-rights-confirmation"
                />
                <span>Я подтверждаю, что имею право использовать этот шрифт в своём бренде и загружать его в Brand Studio.</span>
              </label>
              <input
                ref={ownFontPickerRef}
                type="file"
                accept=".otf,.ttf,.woff,.woff2,font/otf,font/ttf,font/woff,font/woff2"
                className="sr-only"
                onChange={uploadOwnFont}
                data-testid="own-font-upload-input"
              />
              <button
                type="button"
                onClick={() => ownFontPickerRef.current?.click()}
                disabled={busy || !rightsConfirmed}
                className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-full border border-[#CDB899] bg-white px-5 text-[12.5px] text-[#4A3728] disabled:opacity-45"
                data-testid="own-font-upload-button"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Type size={14} />}
                Загрузить OTF / TTF / WOFF / WOFF2
              </button>
              {selectedFontAsset && (
                <div className="mt-4 rounded-[16px] border border-[#DCCDB8] bg-white p-4" data-testid="own-font-uploaded">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#927A5B]">
                    {selectedFontAsset.display_name}
                  </div>
                  <div className="mt-2 text-[11px] text-[#806B55]">{selectedFontAsset.original_filename}</div>
                  <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[11px] text-[#586044]">
                    <Check size={12} /> Файл приватно сохранён
                  </div>

                </div>
              )}
            </div>
          )}

          {mode === "choose_font" && (
            <>
              {catalogBusy ? (
                <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#796652]">
                  <Loader2 size={14} className="animate-spin" /> Открываю каталог шрифтов…
                </div>
              ) : (
                <FontCatalogPicker
                  items={fontCatalog}
                  selectedId={selectedFontId}
                  onSelect={(font) => setSelectedFontId(font.id)}
                  search={fontSearch}
                  onSearch={setFontSearch}
                  category={fontCategory}
                  onCategory={setFontCategory}
                  previewTitle={brandName}
                  fontStatus={fontStatus}
                  role={role} entryFor={entryFor}
                />
              )}
            </>
          )}

          <label className="mt-5 block">
            <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A6D53]">Что особенно важно?</span>
            <textarea value={note} onChange={(event) => setNote(event.target.value)} rows={3} maxLength={800} placeholder={mode === "provided" ? "Например: люблю большие интервалы между буквами и спокойный печатный характер." : "Например: хочется лёгкий рукописный акцент для коротких фраз."} className="mt-2 w-full resize-y rounded-[14px] border border-[#DCCDB8] bg-white px-3.5 py-3 text-[13px] leading-6 text-[#3E2E20] outline-none placeholder:text-[#B09C87] focus:border-[#8C6C49]" data-testid="typography-preferences-note" />
          </label>

          {actionArea ? createPortal(actions, actionArea) : actions}
        </div>
      )}

      {error && !adding && <p className="mt-4 rounded-[12px] border border-[#E2CFC8] bg-[#FFF8F4] p-3 text-[12px] text-[#7A4E43]">{error}</p>}
    </section>
  );
}

